/* global google */
/** @jsx jsx */
import React from 'react';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import debounce from 'lodash/debounce';
import { jsx } from '@emotion/core';
import { Column } from '../../../../../styles/Grid';
import Downshift from 'downshift';
import Lottie from 'react-lottie';
import get from 'lodash/get';
import searchAnimation from '../../../../../assets/animations/search-load-grey.json';
import * as styles from './index.styles';
import {
  MINIMUM_CHARACTERS_TO_SEARCH,
  SEARCH_PARAMETER,
} from '../../../../../utils/constants';
import { compose } from 'lodash/fp';
import { withConsumer } from '../../../../../contexts';
import { RichFilteringConsumer } from '../../../../../contexts/RichFilteringContext';
import { withNamespaces } from 'react-i18next';
import { RICH_FILTERING as dict } from '../../../../../utils/dictionarykeys';
import { getDecodedQueryParams } from '../../../../../utils/url';
import { getCoordinates } from '../../../../../utils/googleMaps';

const STATUS_OK = 'OK';
const PLACES_READY_EVENT = 'google.places.api.ready';

const animationOptions = {
  animationData: searchAnimation,
  autoplay: false,
  loop: true,
};

const fetchAutocompletePlaces = (autocompleteService, options) =>
  new Promise((resolve, reject) => {
    autocompleteService.getQueryPredictions(options, (results, status) => {
      if (status === STATUS_OK) {
        resolve(results);
      } else {
        reject([results, status]);
      }
    });
  });

const fetchPlaceData = (geocodeService, placeId) =>
  new Promise((resolve, reject) => {
    geocodeService.geocode({ placeId }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const [place] = results;
        resolve(place);
      } else {
        reject(results, status);
      }
    });
  });

const getLatLngFromPlace = place => {
  const viewport = get(place, 'geometry.viewport');

  if (!viewport) {
    return;
  }

  return getCoordinates(viewport);
};

class Search extends SafePureComponent {
  searchRef = React.createRef();
  autocompleteService = null;
  geocoder = {};

  state = {
    inputValue: '',
    isFocus: false,
    isLoading: false,
    noResults: false,
    suggestionMenuIsOpen: true,
    suggestions: [],
  };

  componentDidMount() {
    const params = getDecodedQueryParams();

    if (window.placesAPIReady) {
      this.initServices();
    } else {
      window.addEventListener(PLACES_READY_EVENT, this.initServices);
    }

    this.setState({ inputValue: params[SEARCH_PARAMETER] });
  }

  initServices = () => {
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.geocoder = new google.maps.Geocoder();
  };

  onFormSubmit = e => {
    e.preventDefault();
    const input = this.searchRef.current;

    if (input.value <= MINIMUM_CHARACTERS_TO_SEARCH) return;

    const { search } = get(
      this.props,
      'richFilteringContext.state.params',
      null,
    );

    if (input.value !== search) {
      if (this.state.suggestions.length) {
        const { suggestions } = this.state;
        const firstSuggestion = suggestions[0];

        this.searchSuggestionPlace(firstSuggestion);
      } else {
        this.setState({ noResults: true });
      }
    } else return;

    input.blur();
  };

  fetchSuggestions = debounce(async input => {
    const { autocompleteService } = this;
    const autocompleteOptions = { input };

    const places = await fetchAutocompletePlaces(
      autocompleteService,
      autocompleteOptions,
    ).catch(() => []);

    const suggestions = places.map(({ description, place_id }) => ({
      id: place_id,
      description,
    }));

    this.setState({ suggestions });
  }, 300);

  searchSuggestionPlace = async item => {
    const { geocoder } = this;
    const {
      replaceWindowUrl,
      storeUrlParams,
    } = this.props.richFilteringContext.actions;
    const { id, description } = item;

    this.setState({ isLoading: true });

    const place = await fetchPlaceData(geocoder, id);
    const { northeast, southwest } = getLatLngFromPlace(place);

    this.setState({
      inputValue: description,
      isLoading: false,
      suggestionMenuIsOpen: false,
    });

    this.onInputBlur();

    const setValues = {
      northeast,
      southwest,
      search: description,
    };

    replaceWindowUrl(setValues);
    storeUrlParams();
  };

  onInputChange = input => {
    const { value } = input.target;
    this.setState({ inputValue: value });

    if (value.length >= MINIMUM_CHARACTERS_TO_SEARCH) {
      this.fetchSuggestions(value);
    } else {
      this.setState({ suggestions: [] });
    }
  };

  onInputFocus = () => {
    this.setState({
      isFocus: true,
      noResults: false,
      suggestionMenuIsOpen: true,
    });
  };

  onInputBlur = () => {
    this.setState({ isFocus: false });
  };

  renderSuggestions = (
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
  ) => {
    const { suggestions } = this.state;
    if (isOpen && Boolean(suggestions.length)) {
      return (
        <ul css={styles.suggestionsContainer} {...getMenuProps()}>
          {suggestions.map((item, index) => (
            <li
              css={[
                styles.suggestion,
                highlightedIndex === index && styles.highlightedSuggestion,
              ]}
              key={`place-${item.id}`}
              {...getItemProps({ item, index })}
            >
              <i css={styles.suggestionIcon} className="icon-pin" />
              {item.description}
            </li>
          ))}
        </ul>
      );
    }
  };

  renderLoader = () => {
    const { isLoading } = this.state;

    if (!isLoading) {
      return (
        <button
          css={styles.searchIconButton}
          data-testid="form-submit"
          type="submit"
        >
          <i css={styles.searchIcon} className="icon-search" />
        </button>
      );
    }
  };

  hideSuggestions = () => {
    this.setState({ suggestionMenuIsOpen: false });
  };

  safeRender() {
    const { t } = this.props;
    const { isLoading, inputValue, isFocus, noResults } = this.state;

    return (
      <Column cols={{ sm: 6, md: 5, lg: 8, xl: 9 }} css={styles.formContainer}>
        <form onSubmit={this.onFormSubmit}>
          <Downshift
            isOpen={this.state.suggestionMenuIsOpen}
            itemToString={item => get(item, 'description', '')}
            onChange={this.searchSuggestionPlace}
            onOuterClick={this.hideSuggestions}
          >
            {({
              getInputProps,
              getItemProps,
              getMenuProps,
              getRootProps,
              highlightedIndex,
              isOpen,
            }) => (
              <div
                {...getRootProps({}, { suppressRefError: true })}
                css={[
                  styles.searchInputContainer,
                  isFocus && !isLoading && styles.searchInputContainerFocus,
                ]}
              >
                <input
                  {...getInputProps({
                    onChange: this.onInputChange,
                  })}
                  css={styles.searchInput}
                  disabled={isLoading}
                  onBlur={this.onInputBlur}
                  onFocus={this.onInputFocus}
                  placeholder={t(dict.PLACEHOLDER_TEXT_SEARCH)}
                  ref={this.searchRef}
                  value={inputValue}
                />
                <span css={styles.animationContainer}>
                  <Lottie
                    height={50}
                    isClickToPauseDisabled
                    isStopped={!isLoading}
                    options={animationOptions}
                    width={75}
                  />
                </span>
                {this.renderLoader()}
                {this.renderSuggestions(
                  getItemProps,
                  getMenuProps,
                  highlightedIndex,
                  isOpen,
                )}
              </div>
            )}
          </Downshift>
          {noResults && <div css={styles.noResults}>{t(dict.NO_RESULTS)}</div>}
        </form>
      </Column>
    );
  }
}

export default compose(
  withNamespaces(),
  withConsumer(RichFilteringConsumer, 'richFilteringContext'),
)(Search);
